import { useState } from 'react';

import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import { CheckCircle, Loader2 } from 'lucide-react';

import { Badge, Button, Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui';
import { TransactionCategoryEnum, TransactionTypeEnum } from '@/enums';
import { getTitleEnumValue, notify } from '@/functions';
import { useRequestPayTransaction } from '@/hooks/httpRequest';
import { cn } from '@/lib/utils';
import { UserProfileLinkButton } from './UserProfileLinkButton';

import type { IDescriptionDisplayProps, ITransaction, ITransactionDetailsModalProps } from '@/interfaces';

const DescriptionDisplay = ({ text }: IDescriptionDisplayProps) => {
	const [isExpanded, setIsExpanded] = useState(false);

	if (!text) return null;

	const shouldTruncate = text.length > 200;

	return (
		<div className="bg-muted/40 rounded-md p-3 mt-1">
			<div
				className={cn(
					'text-sm whitespace-pre-wrap wrap-break-word transition-all duration-200',
					!isExpanded && shouldTruncate ? 'line-clamp-3' : ''
				)}
			>
				{text}
			</div>
			{shouldTruncate && (
				<Button
					variant="link"
					size="sm"
					onClick={() => setIsExpanded(!isExpanded)}
					className="h-auto p-0 mt-2 text-xs text-muted-foreground hover:text-primary font-medium"
				>
					{isExpanded ? 'Voir moins' : 'Lire la suite'}
				</Button>
			)}
		</div>
	);
};

export const TransactionDetailsModal = ({ isOpen, onClose, transaction }: ITransactionDetailsModalProps) => {
	const { payTransaction, isLoading: isLoadingPay } = useRequestPayTransaction();

	const handleMarkAsPaid = async (trans: ITransaction) => {
		try {
			await payTransaction(trans._id);
			notify('La transaction a été marquée comme payée');
			onClose();
		} catch (error) {
			notify((error as Error)?.message || "Une erreur s'est produite", 'error');
		}
	};

	if (!transaction) return null;

	const data = transaction.data || {};
	const status = transaction.status || data.status || 'PENDING';
	const investorName = transaction.data?.investorName || data.investorName;
	const employeeName = transaction.data?.employeeName || data.employeeName;
	const userName = transaction.data?.userName || data.userName;
	const userId = transaction.data?.userId || data.userId;
	const description = transaction.description || data.description || '';
	const hasDescription = typeof description === 'string' && description.trim().length > 0;
	const mode = transaction.mode || 'MANUAL';

	return (
		<Dialog open={isOpen} onOpenChange={onClose}>
			<DialogContent
				showCloseIcon={true}
				onClickCloseIcon={onClose}
				className="flex flex-col p-0 gap-0 max-w-none w-[calc(100%-2rem)] md:w-lg max-h-[90vh] rounded-2xl border bg-background shadow-lg duration-200 overflow-hidden"
			>
				<DialogHeader className="px-6 pt-6 pb-0">
					<DialogTitle className="flex items-center gap-2">
						Détails de la transaction
						<Badge
							variant="secondary"
							className={cn(
								'text-xs font-semibold ml-2',
								mode === 'AUTO'
									? 'bg-blue-500/20 text-blue-500 hover:bg-blue-500/30'
									: 'bg-emerald-500/20 text-emerald-500 hover:bg-emerald-500/30'
							)}
						>
							{mode}
						</Badge>
					</DialogTitle>
				</DialogHeader>

				<div className="px-6 py-4 overflow-y-auto">
					<div className="space-y-3">
						<div className="flex justify-between items-center">
							<span className="text-sm text-muted-foreground">ID:</span>
							<span className="text-sm font-medium font-mono">{transaction._id}</span>
						</div>
						<div className="flex justify-between items-center">
							<span className="text-sm text-muted-foreground">Type:</span>
							<Badge
								variant="secondary"
								className={cn(
									'text-xs font-semibold',
									transaction.type === TransactionTypeEnum.INCOME
										? 'bg-emerald-500/20 text-emerald-500'
										: 'bg-red-500/20 text-red-500'
								)}
							>
								{transaction.type}
							</Badge>
						</div>
						<div className="flex justify-between items-center">
							<span className="text-sm text-muted-foreground">Catégorie:</span>
							<span className="text-sm font-medium">{getTitleEnumValue('TransactionCategoryEnum', transaction.category) ?? transaction.category}</span>
						</div>
						<div className="flex justify-between items-center">
							<span className="text-sm text-muted-foreground">Montant:</span>
							<span
								className={cn(
									'text-sm font-bold',
									transaction.type === TransactionTypeEnum.INCOME ? 'text-emerald-500' : 'text-red-500'
								)}
							>
								€ {(transaction.amountEur || 0).toLocaleString('fr-FR', { minimumFractionDigits: 2 })}
							</span>
						</div>
						{transaction.category === TransactionCategoryEnum.INVESTOR && status && (
							<div className="flex justify-between items-center">
								<span className="text-sm text-muted-foreground">Statut:</span>
								<Badge
									variant="outline"
									className={cn(
										'text-xs font-semibold',
										status.toUpperCase() === 'PAID'
											? 'border-emerald-500/20 text-emerald-500 bg-emerald-500/5'
											: 'border-amber-500/20 text-amber-500 bg-amber-500/5'
									)}
								>
									{status}
								</Badge>
							</div>
						)}
					</div>

					<div className="border-t my-4" />

					<div className="space-y-2">
						{investorName && (
							<div className="flex justify-between items-center mb-2">
								<span className="text-sm text-muted-foreground">Investisseur:</span>
								<span className="text-sm font-medium">{investorName}</span>
							</div>
						)}

						{transaction.category === TransactionCategoryEnum.SALARY && employeeName && (
							<div className="flex justify-between items-center mb-2">
								<span className="text-sm text-muted-foreground">Employé:</span>
								<span className="text-sm font-medium">{employeeName}</span>
							</div>
						)}

						{transaction.category === TransactionCategoryEnum.IN_APP_PURCHASE && (userName || userId) && (
							<div className="flex justify-between items-center mb-2">
								<span className="text-sm text-muted-foreground">Utilisateur:</span>
								<div className="flex items-center gap-2 min-w-0">
									<span className="text-sm font-medium truncate max-w-[180px] md:max-w-[240px]" title={userName || userId}>
										{userName || userId}
									</span>
									{userId && <UserProfileLinkButton userId={userId} />}
								</div>
							</div>
						)}

						{hasDescription && (
							<>
								<span className="text-sm text-muted-foreground">Description:</span>
								<DescriptionDisplay text={description} />
							</>
						)}
					</div>

					<div className="border-t my-4" />

					<div className="space-y-3">
						<div className="flex justify-between items-center">
							<span className="text-sm text-muted-foreground">Date:</span>
							<span className="text-sm font-medium">
								{format(new Date(transaction.occurredAt || transaction.createdAt || new Date()), 'dd MMM yyyy', { locale: fr })}
							</span>
						</div>
					</div>
				</div>

				<DialogFooter className="px-6 py-4 border-t bg-muted/30">
					<div className="flex flex-col sm:flex-row w-full gap-3 sm:justify-end items-stretch sm:items-center">
						{transaction.category === TransactionCategoryEnum.INVESTOR &&
							(transaction.status || transaction.data?.status || 'PENDING').toUpperCase() === 'PENDING' && (
								<Button
									onClick={() => handleMarkAsPaid(transaction)}
									disabled={isLoadingPay}
									variant="outline"
									className="text-emerald-600 border-emerald-600/20 hover:bg-emerald-50 hover:text-emerald-700 w-full sm:w-auto"
								>
									{isLoadingPay ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <CheckCircle className="h-4 w-4 mr-2" />}
									Marquer payé
								</Button>
							)}
						<div className="flex-1 hidden sm:block" />
						<Button variant="outline" onClick={onClose} className="w-full sm:w-auto">
							Fermer
						</Button>
					</div>
				</DialogFooter>
			</DialogContent>
		</Dialog>
	);
};

export default TransactionDetailsModal;
